import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Sparkles, Target } from "lucide-react";
import PageHero from "../components/PageHero";
import Curriculum from "../components/Curriculum";
import Pillars from "../components/Pillars";
import { SectionHeading } from "../components/SectionHeading";
import useSeo from "../hooks/useSeo";

const principles = [
  {
    icon: GraduationCap,
    title: "NEP 2020 & NCF 2023 aligned",
    text: "A self-curated CBSE curriculum for Kindergarten to Grade VII that blends global concepts with culture, discipline and confidence.",
  },
  {
    icon: Sparkles,
    title: "Conceptual clarity first",
    text: "Concrete materials, enquiry questions and lab work come before the textbook page — so understanding outlasts the exam.",
  },
  {
    icon: Target,
    title: "Competency-based assessment",
    text: "Periodic tests, portfolios, subject enrichment and co-scholastic grading, reported continuously to parents.",
  },
];

export default function AcademicsPage() {
  useSeo({
    title: "Academics & Curriculum",
    path: "/academics",
    description:
      "A self-curated CBSE curriculum for Pre-KG to Grade VII aligned to NEP 2020 and NCF 2023 - play-based foundational years, structured preparatory years and project-based middle school learning at The KPR Academy, Coimbatore.",
    keywords:
      "CBSE curriculum Coimbatore, NEP 2020 school, KPR Academy academics, Pre-KG curriculum, middle school CBSE Coimbatore",
  });
  return (
    <>
      <PageHero
        eyebrow="Academics at KPR"
        title="Curriculum design, stage by stage"
        sub="A CBSE spine with a KPR soul — play in the foundational years, structure in the preparatory years, and real-world projects through middle school."
        crumbs={[{ label: "Academics" }]}
        image="/campus/school-5.jpg"
      />

      <section className="py-24 lg:py-28" style={{ background: "var(--cream)" }}>
        <div className="mx-auto max-w-[1280px] px-5 lg:px-6">
          <SectionHeading
            eyebrow="How we teach"
            title="Three principles behind every lesson plan"
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {principles.map((p, i) => (
              <div
                key={p.title}
                data-reveal=""
                style={{ transitionDelay: `${i * 90}ms`, border: "1px solid rgba(11,59,54,.09)" }}
                className="rounded-2xl bg-white p-8"
              >
                <span
                  className="grid h-13 w-13 place-items-center rounded-xl p-3"
                  style={{ background: "var(--teal-100)", color: "var(--teal-700)" }}
                >
                  <p.icon size={22} strokeWidth={1.6} />
                </span>
                <h3 className="font-display mt-6 text-[1.25rem]" style={{ color: "var(--teal-900)" }}>
                  {p.title}
                </h3>
                <p className="mt-3 text-[0.9rem] leading-relaxed" style={{ color: "var(--muted-ink)" }}>
                  {p.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Curriculum />
      <Pillars />

      <section className="py-20" style={{ background: "var(--teal-900)" }}>
        <div className="mx-auto flex max-w-[1280px] flex-col items-start justify-between gap-8 px-5 lg:flex-row lg:items-center lg:px-6">
          <div>
            <h2 className="font-display text-[1.9rem] leading-snug" style={{ color: "#FBF7F0", fontWeight: 300 }}>
              Want the detailed stage-wise plan?
            </h2>
            <p className="mt-3 text-[0.95rem]" style={{ color: "rgba(251,247,240,.7)" }}>
              Our admissions team walks you through subjects, timings and assessment for your child's grade.
            </p>
          </div>
          <Link
            to="/admissions"
            className="group flex items-center gap-3 rounded-full px-8 py-4 text-[0.88rem] font-semibold"
            style={{ background: "var(--gold)", color: "#fff" }}
          >
            Talk to Admissions
            <ArrowRight size={16} className="group-hover:translate-x-1" style={{ transition: "transform .3s ease" }} />
          </Link>
        </div>
      </section>
    </>
  );
}
